// 말소대행업체 실시간 신규 요청 팝업 (Phase 1)
// - cancel_agency role 에게만 렌더 (다른 role 은 null)
// - Socket 'cancel:new-request' 전역 리스너 + 모달 팝업 + 소리 + Desktop Notification
// - 3개 액션: 수락(status='접수') / 보류(sessionStorage 저장 + 플로팅 뱃지) / 상세 보기
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSocket } from '../utils/socket';
import { API, authFetch } from '../utils/api';

var PENDING_KEY = 'autodoc_cancel_pending';
function loadPending() {
  try { var raw = sessionStorage.getItem(PENDING_KEY); return raw ? JSON.parse(raw) : []; } catch(e) { return []; }
}
function savePending(list) {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(list)); } catch(e) {}
}

function CancelAgencyLivePopup(props) {
  var user = props.user;
  var queueState = useState([]);
  var queue = queueState[0];
  var setQueue = queueState[1];
  var pendingState = useState(loadPending());
  var pending = pendingState[0];
  var setPending = pendingState[1];
  var toastState = useState('');
  var toast = toastState[0];
  var setToast = toastState[1];
  var navigate = useNavigate();
  var audioRef = useRef(null);

  useEffect(function() {
    if (!user || user.role !== 'cancel_agency') return;
    var sock = getSocket();
    if (!sock) return;

    // 브라우저 알림 권한 요청 (비동기)
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch(e) {}

    function onNew(data) {
      setQueue(function(prev) { return prev.concat([data]); });

      // 소리
      try {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(function() {});
        }
      } catch(e) {}

      // 데스크톱 알림
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          var title = '\uD83D\uDD04 \uC0C8 \uB9D0\uC18C \uB300\uD589 \uC694\uCCAD';
          var body = (data.requesterCompany || '') + ' \u00B7 ' + (data.carNo || '') + (data.owner ? ' (' + data.owner + ')' : '');
          new Notification(title, { body: body, icon: '/autodex-icon.svg', tag: 'cancel-' + data.id });
        }
      } catch(e) {}
    }

    sock.on('cancel:new-request', onNew);
    return function() {
      try { sock.off('cancel:new-request', onNew); } catch(e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.role]);

  if (!user || user.role !== 'cancel_agency') return null;

  // 보류함 플로팅 뱃지 + 토스트 (큐 비어있을 때도 렌더)
  var pendingBadge = pending.length > 0 ? (
    <div style={{position:'fixed',bottom:20,right:20,zIndex:9998}}>
      <details style={{background:'#1B3A5C',color:'#fff',borderRadius:12,boxShadow:'0 8px 24px rgba(0,0,0,0.25)',minWidth:240}}>
        <summary style={{padding:'12px 16px',cursor:'pointer',fontWeight:800,listStyle:'none',display:'flex',alignItems:'center',gap:8}}>
          📌 보류함 <span style={{background:'#fbbf24',color:'#1B3A5C',padding:'2px 8px',borderRadius:10,fontSize:12}}>{pending.length}</span>
        </summary>
        <div style={{padding:'8px 8px 12px',maxHeight:300,overflowY:'auto'}}>
          {pending.map(function(p, idx) {
            return (
              <div key={p.id} style={{background:'#fff',color:'#1e293b',borderRadius:8,padding:'8px 10px',marginTop:6,display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700}}>{p.carNo || '-'}</div>
                  <div style={{fontSize:11,color:'#64748b'}}>{p.requesterCompany || ''}</div>
                </div>
                <button onClick={function() { resumePending(idx); }} style={{padding:'6px 10px',borderRadius:6,background:'#2563eb',color:'#fff',border:'none',fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>다시 보기</button>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  ) : null;

  var toastEl = toast ? (
    <div style={{position:'fixed',bottom:80,left:'50%',transform:'translateX(-50%)',background:'#1B3A5C',color:'#fff',padding:'10px 18px',borderRadius:10,zIndex:10001,fontSize:14,fontWeight:700,boxShadow:'0 8px 24px rgba(0,0,0,0.3)'}}>{toast}</div>
  ) : null;

  if (queue.length === 0) {
    return (
      <React.Fragment>
        <audio ref={audioRef} preload="auto" style={{display:'none'}}>
          {/* 짧은 "딩동" 알림 사운드 — 브라우저 기본 (data URL base64 wav, 작은 beep) */}
          <source src="data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAAAAAA==" type="audio/wav"/>
        </audio>
        {pendingBadge}
        {toastEl}
      </React.Fragment>
    );
  }

  // 큐의 첫 번째 요청 표시 (FIFO)
  var cur = queue[0];
  var vinTail = cur.vin ? String(cur.vin).slice(-8) : '';

  function dismiss() {
    setQueue(function(prev) { return prev.slice(1); });
  }
  function goDetail() {
    var id = cur && cur.id;
    dismiss();
    // 특정 요청 highlight 쿼리 전달 → 목록에서 해당 row 스크롤/강조
    navigate('/main/cancel-agency' + (id ? '?highlightId=' + id : ''));
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(function() { setToast(''); }, 2500);
  }

  // 수락 — status='접수' 로 전환. 대행업체 관리 목록에서 진행 중으로 표시됨
  function acceptRequest() {
    if (!cur || !cur.id) return;
    var body = {
      agencyId: user && user.agencyId,
      status: '\uC811\uC218',
      fee: cur.fee || 0,
      requestDate: cur.requestDate || '',
      completedDate: '',
      memo: cur.memo || '',
      carId: cur.carId,
    };
    authFetch(API + '/cancel-requests/' + cur.id, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function(r) { return r.json(); })
      .then(function() {
        showToast('\u2705 \uC218\uB77D \uC644\uB8CC');
        // 대행업체 관리 목록에 실시간 반영 (window custom event)
        try { window.dispatchEvent(new CustomEvent('cancel-request-updated', { detail: { id: cur.id, status: '\uC811\uC218' } })); } catch(e) {}
        dismiss();
      })
      .catch(function() { showToast('\u274C \uC218\uB77D \uC2E4\uD328'); });
  }

  // 보류 — sessionStorage 에 저장, 플로팅 뱃지 클릭으로 재팝업
  function holdForLater() {
    if (!cur || !cur.id) return dismiss();
    setPending(function(prev) {
      // 중복 방지 (같은 id 이미 있으면 skip)
      if (prev.some(function(p) { return p.id === cur.id; })) return prev;
      var next = prev.concat([cur]);
      savePending(next);
      return next;
    });
    showToast('\uD83D\uDCCC \uBCF4\uB958\uD568\uC5D0 \uC800\uC7A5\uB428');
    dismiss();
  }

  // 뱃지 클릭 — 보류 목록에서 하나를 다시 큐 맨 앞에 표시
  function resumePending(index) {
    var item = pending[index];
    if (!item) return;
    setPending(function(prev) {
      var next = prev.slice(0, index).concat(prev.slice(index + 1));
      savePending(next);
      return next;
    });
    setQueue(function(prev) { return [item].concat(prev); });
  }

  return (
    <div>
      <audio ref={audioRef} preload="auto" style={{display:'none'}}>
        <source src="data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAAAAAA==" type="audio/wav"/>
      </audio>
      <div style={{
        position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:10000,
        display:'flex',alignItems:'center',justifyContent:'center',padding:16
      }}>
        <div style={{
          background:'#fff',borderRadius:16,maxWidth:440,width:'100%',
          boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden',
          animation:'cancelPopupIn 0.25s ease-out'
        }}>
          <div style={{background:'#1B3A5C',color:'#fff',padding:'14px 18px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontSize:14,fontWeight:800,letterSpacing:0.5}}>🔔 새 말소 대행 요청</div>
            {queue.length > 1 && <div style={{fontSize:12,background:'#fbbf24',color:'#1B3A5C',padding:'2px 8px',borderRadius:10,fontWeight:700}}>+{queue.length - 1}건 대기</div>}
          </div>
          <div style={{padding:'18px 20px'}}>
            <div style={{fontSize:13,color:'#64748b',marginBottom:6}}>요청 업체</div>
            <div style={{fontSize:16,fontWeight:700,color:'#1e293b',marginBottom:14}}>{cur.requesterCompany || '-'}</div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
              <div>
                <div style={{fontSize:12,color:'#64748b'}}>차량번호</div>
                <div style={{fontSize:18,fontWeight:800,color:'#1B3A5C'}}>{cur.carNo || '-'}</div>
              </div>
              <div>
                <div style={{fontSize:12,color:'#64748b'}}>차종</div>
                <div style={{fontSize:14,fontWeight:600,color:'#1e293b',paddingTop:3}}>{cur.carType || '-'}</div>
              </div>
            </div>

            {vinTail && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,color:'#64748b'}}>차대번호 (뒤 8자리)</div>
                <div style={{fontSize:15,fontWeight:700,color:'#1e293b',fontFamily:'monospace',letterSpacing:1}}>{vinTail}</div>
              </div>
            )}

            {cur.memo && (
              <div style={{marginBottom:12,background:'#fef3c7',border:'1px solid #fcd34d',padding:'8px 10px',borderRadius:8}}>
                <div style={{fontSize:11,color:'#92400e',fontWeight:700,marginBottom:2}}>📝 메모</div>
                <div style={{fontSize:13,color:'#78350f'}}>{cur.memo}</div>
              </div>
            )}

            {cur.needsReissue ? (
              <div style={{marginBottom:12,background:'#fee2e2',border:'1px solid #fca5a5',padding:'8px 10px',borderRadius:8,fontSize:12,color:'#b91c1c',fontWeight:700}}>
                ⚠️ 등록증 재교부 필요
              </div>
            ) : null}

            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={holdForLater} style={{flex:1,padding:'12px',borderRadius:8,background:'#fff',color:'#64748b',fontSize:13,fontWeight:700,border:'1px solid #cbd5e1',cursor:'pointer'}}>📌 보류</button>
              <button onClick={goDetail} style={{flex:1,padding:'12px',borderRadius:8,background:'#f1f5f9',color:'#1B3A5C',fontSize:13,fontWeight:700,border:'1px solid #cbd5e1',cursor:'pointer'}}>상세 보기</button>
              <button onClick={acceptRequest} style={{flex:2,padding:'12px',borderRadius:8,background:'#16a34a',color:'#fff',fontSize:14,fontWeight:800,border:'none',cursor:'pointer'}}>✅ 수락</button>
            </div>
          </div>
        </div>
      </div>
      {pendingBadge}
      {toastEl}
      <style>{'@keyframes cancelPopupIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }'}</style>
    </div>
  );
}

export default CancelAgencyLivePopup;
